import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  API_URL,
  getToken,
  setToken,
  getStoredTeacher,
  setStoredTeacher,
  clearAuth,
  setStudentSession as persistStudentSession,
  getStudentSession,
  clearStudentSession,
  getSessionType,
  SESSION_TYPE_STUDENT,
  SESSION_TYPE_TEACHER,
} from '../config';

const AuthContext = createContext(null);

// AuthProvider exposes whichever session is active (teacher or student) so
// downstream UI can branch — `sessionType` is the discriminator.
export function AuthProvider({ children }) {
  const [teacher, setTeacher] = useState(getStoredTeacher);
  const [studentSession, setStudentSessionState] = useState(getStudentSession);
  const [sessionType, setSessionType] = useState(getSessionType());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (sessionType !== SESSION_TYPE_TEACHER) {
      setLoading(false);
      return;
    }
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }
    fetch(`${API_URL}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => {
        if (!res.ok) throw new Error('invalid token');
        return res.json();
      })
      .then(data => {
        setTeacher(data);
        setStoredTeacher(data);
      })
      .catch(() => {
        clearAuth();
        setTeacher(null);
      })
      .finally(() => setLoading(false));
  }, [sessionType]);

  const login = useCallback((token, teacherData) => {
    setToken(token);
    setStoredTeacher(teacherData);
    setTeacher(teacherData);
    setSessionType(SESSION_TYPE_TEACHER);
  }, []);

  const refreshTeacher = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setTeacher(data);
      setStoredTeacher(data);
    } catch { /* ignore */ }
  }, []);

  const logout = useCallback(() => {
    clearAuth();
    setTeacher(null);
    setSessionType(getSessionType());
  }, []);

  // Switch the active session to a freshly-joined student class.
  const loginStudent = useCallback((token, session) => {
    persistStudentSession(token, session);
    setStudentSessionState(session);
    setSessionType(SESSION_TYPE_STUDENT);
  }, []);

  const logoutStudent = useCallback(() => {
    clearStudentSession();
    setStudentSessionState(null);
    setSessionType(getSessionType());
  }, []);

  return (
    <AuthContext.Provider value={{
      teacher,
      studentSession,
      sessionType,
      loading,
      login,
      logout,
      loginStudent,
      logoutStudent,
      refreshTeacher,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
